// src/modules/cv/FeaturesLab.jsx
// Features Lab — Harris Corners, Otsu Threshold, Adaptive Threshold, Histogram Equalization

import React, { useState, useCallback, useEffect } from 'react';
import { generateSynth, imgToImageData, imageDataToURL, harrisCorners, otsuThreshold, histEqualize, toGray, convolve, KERNELS, cloneID } from '../../utils/cvUtils.js';
import { Panel, InfoBox, Metric } from '../../components/ui.jsx';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const TABS = ['upload', 'harris', 'otsu', 'adaptive', 'equalize', 'concepts'];
const Btn = ({onClick,children,color='var(--red)',disabled})=>(<button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1}}>{children}</button>);

function adaptiveThreshold(id, blockSize=15, C=5) {
  const gray = toGray(id);
  const {width:w,height:h,data:src} = gray;
  const dst = new Uint8ClampedArray(src.length);
  const half = Math.floor(blockSize/2);
  for(let y=0;y<h;y++) for(let x=0;x<w;x++){
    let sum=0,n=0;
    for(let dy=-half;dy<=half;dy++) for(let dx=-half;dx<=half;dx++){
      const py=Math.min(h-1,Math.max(0,y+dy)),px=Math.min(w-1,Math.max(0,x+dx));
      sum+=src[(py*w+px)*4];n++;
    }
    const thresh=(sum/n)-C;
    const v=src[(y*w+x)*4]>thresh?255:0;
    const i=(y*w+x)*4;dst[i]=dst[i+1]=dst[i+2]=v;dst[i+3]=255;
  }
  return new ImageData(dst,w,h);
}

const SYNTH = [{name:'Shapes',type:'shapes'},{name:'Checker',type:'checker'},{name:'Circles',type:'circles'},{name:'Portrait',type:'portrait'}];

const CONCEPTS = [
  {title:'Harris Corner Detector',color:'var(--red)',desc:'Computes second-moment matrix M = [Ixx Ixy; Ixy Iyy] in a local window. Corner response R = det(M) − k·trace(M)². R>0 = corner, R<0 = edge, R≈0 = flat. k typically 0.04–0.06.'},
  {title:'Otsu Thresholding',color:'var(--gold)',desc:'Automatically finds optimal threshold by maximizing inter-class variance between foreground/background. No manual tuning needed. Assumes bimodal histogram. O(256) algorithm.'},
  {title:'Adaptive Threshold',color:'var(--violet)',desc:'Computes local mean in each block, subtracts constant C. Handles uneven illumination (shadows, gradients). Better than global Otsu for text on non-uniform backgrounds.'},
  {title:'Histogram Equalization',color:'var(--cyan)',desc:'Redistributes intensity values so the CDF becomes linear. Uses cumulative distribution function (CDF): new_v = (CDF(v) − CDF_min) / (n − CDF_min) × 255. Enhances contrast in flat-histogram images.'},
];

export default function FeaturesLab({ seedImageSrc = null, hideUpload = false }) {
  const [tab, setTab] = useState(hideUpload ? 'harris' : 'upload');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [corners, setCorners] = useState([]);
  const [harrisK, setHarrisK] = useState(0.04);
  const [harrisThresh, setHarrisThresh] = useState(0.01);
  const [otsuT, setOtsuT] = useState(null);
  const [otsuSrc, setOtsuSrc] = useState(null);
  const [adaptSrc, setAdaptSrc] = useState(null);
  const [blockSize, setBlockSize] = useState(15);
  const [adaptC, setAdaptC] = useState(5);
  const [equSrc, setEquSrc] = useState(null);

  const loadSrc = useCallback(async (src) => {
    setImgSrc(src); const id = await imgToImageData(src); setImgData(id);
    setCorners([]); setOtsuT(null); setOtsuSrc(null); setAdaptSrc(null); setEquSrc(null);
    if (tab==='upload') setTab('harris');
  }, [tab]);

  useEffect(() => {
    if (seedImageSrc) loadSrc(seedImageSrc);
  }, [seedImageSrc, loadSrc]);

  useEffect(() => {
    if (hideUpload && tab === 'upload') setTab('harris');
  }, [hideUpload, tab]);

  const handleFile = (file) => {
    if (!file?.type.startsWith('image/')) return;
    const r = new FileReader(); r.onload = e => loadSrc(e.target.result); r.readAsDataURL(file);
  };

  const runHarris = () => {
    if (!imgData) return;
    const c = harrisCorners(imgData, harrisK, harrisThresh);
    setCorners(c);
  };

  const runOtsu = () => {
    if (!imgData) return;
    const t = otsuThreshold(imgData);
    setOtsuT(t);
    const d = cloneID(toGray(imgData));
    for(let i=0;i<d.data.length;i+=4){const v=d.data[i]>t?255:0;d.data[i]=d.data[i+1]=d.data[i+2]=v;}
    setOtsuSrc(imageDataToURL(d));
  };

  const runAdaptive = () => {
    if (!imgData) return;
    const bs = blockSize%2===0 ? blockSize+1 : blockSize;
    setAdaptSrc(imageDataToURL(adaptiveThreshold(imgData, bs, adaptC)));
  };

  const runEqualize = () => {
    if (!imgData) return;
    setEquSrc(imageDataToURL(histEqualize(imgData)));
  };

  const drawHarrisOverlay = () => {
    if (!imgSrc || !corners.length) return imgSrc;
    const canvas = document.createElement('canvas');
    const img = new Image(); img.src = imgSrc;
    canvas.width = img.naturalWidth || 256; canvas.height = img.naturalHeight || 256;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    corners.slice(0,40).forEach(({x,y,score})=>{
      ctx.strokeStyle=`rgba(255,50,50,${0.4+score*0.6})`; ctx.lineWidth=2;
      ctx.beginPath(); ctx.arc(x,y,4,0,Math.PI*2); ctx.stroke();
      ctx.fillStyle=`rgba(255,50,50,0.8)`;ctx.beginPath();ctx.arc(x,y,2,0,Math.PI*2);ctx.fill();
    });
    return canvas.toDataURL();
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {(hideUpload ? TABS.filter(t => t !== 'upload') : TABS).map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--red)':'transparent'}`,background:tab===t?'var(--red)18':'transparent',color:tab===t?'var(--red)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {tab==='upload' && !hideUpload && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}} onDragOver={e=>e.preventDefault()} onClick={()=>document.getElementById('feat-file').click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input id="feat-file" type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>◐</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop Image</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Harris works on geometric shapes. Threshold on any image.</div>
          </div>
          <Panel title="SYNTHETIC TEST IMAGES" accent="var(--red)">
            {SYNTH.map(({name,type})=>(
              <div key={type} onClick={()=>loadSrc(generateSynth(type,256))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 12px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--red)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div></div>
                <span style={{color:'var(--red)'}}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {tab==='harris' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="HARRIS CORNER DETECTOR" accent="var(--red)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
              <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>k parameter</span><span style={{fontSize:'10px',color:'var(--red)',fontWeight:700}}>{harrisK.toFixed(3)}</span></div><input type="range" min={0.01} max={0.1} step={.005} value={harrisK} onChange={e=>setHarrisK(+e.target.value)} style={{width:'100%',accentColor:'var(--red)'}}/></div>
              <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>Threshold</span><span style={{fontSize:'10px',color:'var(--red)',fontWeight:700}}>{harrisThresh.toFixed(3)}</span></div><input type="range" min={0.001} max={0.1} step={.001} value={harrisThresh} onChange={e=>setHarrisThresh(+e.target.value)} style={{width:'100%',accentColor:'var(--red)'}}/></div>
            </div>
            <Btn onClick={runHarris}>DETECT CORNERS →</Btn>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'12px'}}>
            <Panel title={`DETECTED CORNERS (${corners.length})`} accent="var(--red)">
              {corners.length > 0 ? <img src={drawHarrisOverlay()} alt="harris" style={{width:'100%',borderRadius:'3px'}}/> : <img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/>}
            </Panel>
            <Panel title="TOP CORNERS" accent="var(--red)">
              {corners.length > 0 ? (
                <div style={{overflowY:'auto',maxHeight:'300px'}}>
                  {corners.slice(0,20).map((c,i)=>(
                    <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid var(--b0)',fontSize:'10px'}}>
                      <span style={{color:'var(--t3)'}}>#{i+1}</span>
                      <span style={{color:'var(--t2)'}}>({c.x},{c.y})</span>
                      <span style={{color:'var(--red)',fontWeight:700}}>{c.score}</span>
                    </div>
                  ))}
                </div>
              ) : <InfoBox color="var(--t3)" icon="◐">Run detection first.</InfoBox>}
            </Panel>
          </div>
        </div>
      )}

      {tab==='otsu' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="OTSU AUTOMATIC THRESHOLDING" accent="var(--gold)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              Maximizes inter-class variance σ²_B = wB·wF·(μB−μF)². Automatically finds the best global threshold for bimodal histograms. No parameter tuning needed.
            </div>
            <Btn onClick={runOtsu} color="var(--gold)">COMPUTE OTSU →</Btn>
            {otsuT !== null && <div style={{marginTop:'10px',padding:'10px 14px',background:'var(--ggold)',border:'1px solid var(--gold)44',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'13px',color:'var(--gold)'}}>Optimal threshold: T = {otsuT} (of 0–255)</div>}
          </Panel>
          {otsuSrc && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <Panel title="ORIGINAL" accent="var(--t2)"><img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/></Panel>
              <Panel title={`OTSU BINARY (T=${otsuT})`} accent="var(--gold)"><img src={otsuSrc} alt="otsu" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--gold)44'}}/></Panel>
            </div>
          )}
        </div>
      )}

      {tab==='adaptive' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ADAPTIVE THRESHOLDING" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              Computes a local mean in each block of (blockSize×blockSize) pixels, then thresholds at mean−C. Handles uneven illumination — ideal for documents, text on shaded backgrounds.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
              <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>Block Size</span><span style={{fontSize:'10px',color:'var(--violet)',fontWeight:700}}>{blockSize%2===0?blockSize+1:blockSize}px</span></div><input type="range" min={5} max={51} step={2} value={blockSize} onChange={e=>setBlockSize(+e.target.value)} style={{width:'100%',accentColor:'var(--violet)'}}/></div>
              <div><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>Constant C</span><span style={{fontSize:'10px',color:'var(--violet)',fontWeight:700}}>{adaptC}</span></div><input type="range" min={-20} max={30} step={1} value={adaptC} onChange={e=>setAdaptC(+e.target.value)} style={{width:'100%',accentColor:'var(--violet)'}}/></div>
            </div>
            <Btn onClick={runAdaptive} color="var(--violet)">APPLY ADAPTIVE →</Btn>
          </Panel>
          {adaptSrc && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <Panel title="ORIGINAL" accent="var(--t2)"><img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/></Panel>
              <Panel title="ADAPTIVE RESULT" accent="var(--violet)"><img src={adaptSrc} alt="adapt" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--violet)44'}}/></Panel>
            </div>
          )}
        </div>
      )}

      {tab==='equalize' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="HISTOGRAM EQUALIZATION" accent="var(--cyan)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7,marginBottom:'12px'}}>
              Redistributes pixel intensities using the CDF so that all intensity levels are equally probable. Enhances contrast in flat-histogram images. new_v = (CDF(v) − CDF_min) / (n − CDF_min) × 255.
            </div>
            <Btn onClick={runEqualize} color="var(--cyan)">EQUALIZE HISTOGRAM →</Btn>
          </Panel>
          {equSrc && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <Panel title="ORIGINAL" accent="var(--t2)"><img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/></Panel>
              <Panel title="EQUALIZED" accent="var(--cyan)"><img src={equSrc} alt="equ" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--cyan)44'}}/></Panel>
            </div>
          )}
        </div>
      )}

      {tab==='concepts' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          {CONCEPTS.map(({title,color,desc})=>(
            <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
              <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
              <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
            </div>
          ))}
        </div>
      )}

      {!imgData && tab!=='upload' && tab!=='concepts' && <InfoBox color="var(--red)" icon="◐">Go to UPLOAD tab to load an image first.</InfoBox>}
    </div>
  );
}
